import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Clock, Users, Building, IdCard, Loader2, AlertTriangle, Search, CheckCircle2, UserPlus, X, History, RotateCcw, ShieldCheck } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { resolveImg } from "@/lib/imageUtils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

export default function OrgCourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role_code === "super_admin";
  // Super-Admin impersonation: allow ?org_id=<id> in the URL so the
  // platform owner can manage assignments for any tenant from the
  // same UI. When present, every API call on this page carries the
  // `organization_id` query param, which the backend `_org_for()`
  // helper consumes (super_admin only — Hospital Admins ignore it).
  const [searchParams] = useSearchParams();
  const impersonateOrgId = searchParams.get("org_id") || "";
  const apiParams = useMemo(() => (
    impersonateOrgId ? { organization_id: impersonateOrgId } : {}
  ), [impersonateOrgId]);
  const [tab, setTab] = useState("overview");

  // Super Admin: "Recently Removed" audit tab state
  const [removedList, setRemovedList] = useState([]);
  const [removedLoading, setRemovedLoading] = useState(false);
  const [removedQuery, setRemovedQuery] = useState("");
  const [reassigningId, setReassigningId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedDesigs, setSelectedDesigs] = useState([]);
  const [deptMand, setDeptMand] = useState("optional");
  const [desigMand, setDesigMand] = useState("optional");
  const [savingDept, setSavingDept] = useState(false);
  const [savingDesig, setSavingDesig] = useState(false);

  const [assignDlg, setAssignDlg] = useState(null); // "department"|"designation"|"all_staff"
  const [assignForm, setAssignForm] = useState({ target_id: "", due_date: "", mandatory_or_optional: "optional" });
  const [assigning, setAssigning] = useState(false);

  // Bulk enrol + Bill dialog
  const [billOpen, setBillOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState({}); // {user_id: true}
  const [staffQuery, setStaffQuery] = useState("");
  const [billForm, setBillForm] = useState({
    unit_price: "", payment_method: "bank_transfer",
    payment_reference: "", send_email: true,
  });
  const [billing, setBilling] = useState(false);

  // Individual "Assign to selected staff" dialog — reuses the same
  // staff picker as the bill flow but posts to /individual (no invoice).
  const [assignIndivOpen, setAssignIndivOpen] = useState(false);
  const [assignIndivMand, setAssignIndivMand] = useState("optional");
  const [assignIndivDue, setAssignIndivDue] = useState("");
  const [assigningIndiv, setAssigningIndiv] = useState(false);

  // "View assignments" dialog — click "Assignments: 7" to open, allows
  // per-row Unassign (PATCH status=cancelled).
  const [assignmentsOpen, setAssignmentsOpen] = useState(false);
  const [assignmentsList, setAssignmentsList] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsQuery, setAssignmentsQuery] = useState("");
  const [unassigningId, setUnassigningId] = useState(null);

  const filteredStaff = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter((s) =>
      (s.full_name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.department_name || "").toLowerCase().includes(q) ||
      (s.designation || "").toLowerCase().includes(q)
    );
  }, [staffList, staffQuery]);

  const selectedCount = useMemo(
    () => Object.values(selectedStaff).filter(Boolean).length,
    [selectedStaff]
  );

  const openBillDialog = async () => {
    setBillOpen(true);
    setStaffQuery("");
    try {
      const { data } = await api.get("/org/staff?limit=500");
      const list = data.data?.staff || data.data?.users || [];
      setStaffList(list);
      // preselect all by default
      const pre = {}; list.forEach((s) => { pre[s.id] = true; });
      setSelectedStaff(pre);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleStaff = (uid) => setSelectedStaff((prev) => ({ ...prev, [uid]: !prev[uid] }));
  const setAllStaff = (val) => {
    const next = {};
    filteredStaff.forEach((s) => { next[s.id] = val; });
    setSelectedStaff((prev) => ({ ...prev, ...next }));
  };

  const submitBulkBill = async () => {
    const ids = Object.entries(selectedStaff).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.error("Pick at least one staff member."); return; }
    const price = Number(billForm.unit_price);
    if (!price || price <= 0) { toast.error("Enter a valid unit price."); return; }
    const orgId = m?.organization_id;
    if (!orgId) { toast.error("Organization ID missing on this course."); return; }
    setBilling(true);
    try {
      const { data } = await api.post(`/admin/organizations/${orgId}/bulk-invoice`, {
        course_id: courseId, user_ids: ids,
        unit_price: price,
        payment_method: billForm.payment_method,
        payment_reference: billForm.payment_reference || "",
        send_email: billForm.send_email,
      });
      const invNo = data?.data?.invoice?.invoice_number;
      toast.success(`Invoice ${invNo} created for ${ids.length} staff${billForm.send_email ? " · emailed to institute" : ""}.`);
      setBillOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBilling(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/org/courses/${courseId}`, { params: apiParams });
      setPayload(data.data);
      setSelectedDepts((data.data.department_mappings || []).map((m) => m.department_id));
      setSelectedDesigs((data.data.designation_mappings || []).map((m) => m.designation_id));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [courseId, apiParams]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [d, dg] = await Promise.all([api.get("/org/departments"), api.get("/org/designations")]);
        setDepts(d.data.data.departments);
        setDesigs(dg.data.data.designations);
      } catch { /* silent */ }
    })();
  }, []);

  const saveDeptMapping = async () => {
    setSavingDept(true);
    try {
      await api.post(`/org/courses/${courseId}/map-departments`, { department_ids: selectedDepts, mandatory_or_optional: deptMand });
      toast.success("Department mapping updated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingDept(false); }
  };

  const saveDesigMapping = async () => {
    setSavingDesig(true);
    try {
      await api.post(`/org/courses/${courseId}/map-designations`, { designation_ids: selectedDesigs, mandatory_or_optional: desigMand });
      toast.success("Designation mapping updated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingDesig(false); }
  };

  const submitAssign = async () => {
    setAssigning(true);
    try {
      const body = { course_id: courseId, mandatory_or_optional: assignForm.mandatory_or_optional };
      if (assignForm.due_date) body.due_date = assignForm.due_date;
      let ep = "";
      if (assignDlg === "department") { ep = "/org/course-assignments/department"; body.department_id = assignForm.target_id; }
      else if (assignDlg === "designation") { ep = "/org/course-assignments/designation"; body.designation_id = assignForm.target_id; }
      else if (assignDlg === "all_staff") { ep = "/org/course-assignments/all-staff"; }
      const { data } = await api.post(ep, body);
      toast.success(data.message);
      setAssignDlg(null);
      setAssignForm({ target_id: "", due_date: "", mandatory_or_optional: "optional" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAssigning(false); }
  };

  // Open the individual-assignment dialog. Reuses the same staff list
  // as the bill flow; nothing pre-selected here (opposite of Bill flow
  // which auto-selects everyone).
  const openAssignIndividual = async () => {
    setAssignIndivOpen(true);
    setStaffQuery("");
    setSelectedStaff({});
    setAssignIndivMand(m?.mandatory_or_optional || "optional");
    setAssignIndivDue("");
    try {
      const { data } = await api.get("/org/staff", { params: { ...apiParams, limit: 500 } });
      setStaffList(data.data?.staff || data.data?.users || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitIndividualAssign = async () => {
    const ids = Object.entries(selectedStaff).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.error("Pick at least one staff member."); return; }
    setAssigningIndiv(true);
    try {
      const body = {
        course_id: courseId, user_ids: ids,
        mandatory_or_optional: assignIndivMand,
      };
      if (assignIndivDue) body.due_date = assignIndivDue;
      const { data } = await api.post("/org/course-assignments/individual", body, { params: apiParams });
      toast.success(data.message);
      setAssignIndivOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAssigningIndiv(false); }
  };

  // Open the "view assignments" dialog and fetch current rows.
  const openAssignmentsDialog = async () => {
    setAssignmentsOpen(true);
    setAssignmentsQuery("");
    setAssignmentsLoading(true);
    try {
      const { data } = await api.get(`/org/course-assignments`, { params: { ...apiParams, course_id: courseId, page_size: 200 } });
      setAssignmentsList((data.data?.assignments) || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAssignmentsLoading(false); }
  };

  const unassignOne = async (assignmentId, userName) => {
    const ok = window.confirm(
      `Remove this course from ${userName || "this staff"}?\n\n` +
      `• Course "${(payload?.course?.course_title) || ""}" will disappear from their "My courses" immediately.\n` +
      `• Their watched-video progress is preserved — if you assign the course again later, they'll resume from where they left off.`
    );
    if (!ok) return;
    setUnassigningId(assignmentId);
    try {
      // Hard-delete the assignment row so the course fully disappears
      // from the learner side (soft-cancel would still leave the row
      // hanging around with status=cancelled).
      await api.delete(`/org/course-assignments/${assignmentId}`, { params: apiParams });
      toast.success("Course removed for this staff.");
      setAssignmentsList((prev) => prev.filter((a) => a.id !== assignmentId));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUnassigningId(null); }
  };

  const filteredAssignments = useMemo(() => {
    const q = assignmentsQuery.trim().toLowerCase();
    if (!q) return assignmentsList;
    return assignmentsList.filter((a) =>
      (a.user?.full_name || "").toLowerCase().includes(q) ||
      (a.user?.email || "").toLowerCase().includes(q) ||
      (a.user?.employee_id || "").toLowerCase().includes(q)
    );
  }, [assignmentsList, assignmentsQuery]);

  // Super Admin: load the audit list of removed assignments for this course.
  const loadRemoved = useCallback(async () => {
    if (!isSuperAdmin) return;
    setRemovedLoading(true);
    try {
      const { data } = await api.get(`/org/removed-assignments`, {
        params: { ...apiParams, course_id: courseId, page_size: 200 },
      });
      setRemovedList(data.data?.removals || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRemovedLoading(false); }
  }, [isSuperAdmin, apiParams, courseId]);

  // Lazy-load when the tab is activated
  useEffect(() => {
    if (tab === "removed" && isSuperAdmin) loadRemoved();
  }, [tab, isSuperAdmin, loadRemoved]);

  const reassignRemoved = async (removalId, staffName) => {
    const ok = window.confirm(
      `Re-assign this course to ${staffName || "this staff"}?\n\n` +
      `A fresh assignment will be created. Their previous progress (video watch %) will resume from where it left off.`
    );
    if (!ok) return;
    setReassigningId(removalId);
    try {
      const { data } = await api.post(`/org/removed-assignments/${removalId}/reassign`, {}, { params: apiParams });
      toast.success(data.message || "Staff re-assigned.");
      loadRemoved();
      load(); // refresh assignments_count on the header
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReassigningId(null); }
  };

  const filteredRemoved = useMemo(() => {
    const q = removedQuery.trim().toLowerCase();
    if (!q) return removedList;
    return removedList.filter((r) =>
      (r.staff_name || "").toLowerCase().includes(q) ||
      (r.staff_email || "").toLowerCase().includes(q) ||
      (r.staff_employee_id || "").toLowerCase().includes(q) ||
      (r.removed_by_name || "").toLowerCase().includes(q)
    );
  }, [removedList, removedQuery]);

  if (loading) return <DashboardLayout title="Course"><LoadingBlock label="Loading course…" /></DashboardLayout>;
  if (!payload) return null;
  const c = payload.course;
  const m = payload.mapping;

  return (
    <DashboardLayout title={c.course_title} breadcrumbs={["My Institute", "Courses", c.course_title]}>
      <div className="space-y-6" data-testid="org-course-detail">
        <Link to="/app/hospital/courses" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Mapped courses
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {c.banner ? <img src={resolveImg(c.banner)} alt="" className="h-32 w-full object-cover" /> :
            <div className="h-24 w-full bg-gradient-to-br from-[#101876] to-[#329112]" />}
          <div className="p-6 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{c.category_name}</div>
                <div className="mt-1 text-2xl font-black text-slate-900">{c.course_title}</div>
                <div className="mt-1 text-xs text-slate-500 font-mono">{c.course_code}</div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                  <span className="capitalize">{c.course_type?.replace(/_/g, " ")}</span>
                  <span>·</span>
                  <span className="capitalize">{c.course_level}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.estimated_duration_hours}h</span>
                  <span>·</span>
                  <span>v{c.current_version}</span>
                  {m?.mandatory_or_optional === "mandatory" && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 font-bold text-rose-700"><AlertTriangle className="h-3 w-3" /> Mandatory</span>}
                </div>
              </div>
              <div className="text-right space-y-1 text-xs">
                <div><span className="text-slate-500">Access:</span> {m?.access_start_date || "—"} → {m?.access_end_date || "no expiry"}</div>
                <div>
                  <span className="text-slate-500">Assignments:</span>{" "}
                  <button
                    onClick={openAssignmentsDialog}
                    data-testid="org-view-assignments-btn"
                    className="font-bold text-[#101876] underline decoration-dotted underline-offset-2 hover:text-[#0c135c]"
                    title="View / un-assign staff"
                  >
                    {payload.assignments_count}
                  </button>
                </div>
              </div>
            </div>
            {c.short_description && <p className="text-sm text-slate-700">{c.short_description}</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap gap-2">
          <button onClick={openAssignIndividual} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid="org-assign-indiv-btn">
            <UserPlus className="h-4 w-4" /> Assign to selected staff
          </button>
          <button onClick={openAssignmentsDialog} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100" data-testid="org-manage-assignments-btn">
            <X className="h-4 w-4" /> Manage / Remove assignments
            {payload?.assignments_count > 0 && (
              <span className="ml-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">{payload.assignments_count}</span>
            )}
          </button>
          <button onClick={() => { setAssignDlg("department"); setAssignForm({ target_id: "", due_date: "", mandatory_or_optional: m?.mandatory_or_optional || "optional" }); }} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid="org-assign-dept-btn">
            <Building className="h-4 w-4" /> Assign to department
          </button>
          <button onClick={() => { setAssignDlg("designation"); setAssignForm({ target_id: "", due_date: "", mandatory_or_optional: m?.mandatory_or_optional || "optional" }); }} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid="org-assign-desig-btn">
            <IdCard className="h-4 w-4" /> Assign to designation
          </button>
          <button onClick={() => { setAssignDlg("all_staff"); setAssignForm({ target_id: "", due_date: "", mandatory_or_optional: m?.mandatory_or_optional || "optional" }); }} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid="org-assign-all-btn">
            <Users className="h-4 w-4" /> Assign to all staff
          </button>
          <button onClick={() => navigate(`/app/hospital/courses/bulk-assign?course=${courseId}`)} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="org-bulk-assign-btn">
            <Users className="h-4 w-4" /> Bulk assign (with preview)
          </button>
          <button
            onClick={openBillDialog}
            className="inline-flex items-center gap-1.5 rounded-md border-2 border-[#329112] bg-white px-4 py-2 text-sm font-bold text-[#329112] hover:bg-[#329112]/5"
            data-testid="org-bulk-bill-btn"
          >
            <Users className="h-4 w-4" /> Bulk enrol + Bill hospital
          </button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="org-course-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="curriculum" data-testid="org-course-tab-curriculum">Curriculum</TabsTrigger>
            <TabsTrigger value="dept-map" data-testid="org-course-tab-dept">Department mapping</TabsTrigger>
            <TabsTrigger value="desig-map" data-testid="org-course-tab-desig">Designation mapping</TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="removed" data-testid="org-course-tab-removed" className="flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> Recently Removed
                <ShieldCheck className="h-3 w-3 text-[#329112]" />
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
              {c.detailed_description && <ParaBlock title="Detailed description" text={c.detailed_description} />}
              {c.course_objective && <ParaBlock title="Objective" text={c.course_objective} />}
              {c.learning_outcomes && <ParaBlock title="Learning outcomes" text={c.learning_outcomes} />}
              {c.target_audience && <ParaBlock title="Target audience" text={c.target_audience} />}
              {c.prerequisites && <ParaBlock title="Prerequisites" text={c.prerequisites} />}
              {(c.applicable_standards || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Applicable standards</div>
                  <div className="mt-1 flex flex-wrap gap-2">{c.applicable_standards.map((s) => <span key={s} className="rounded-full bg-[#101876]/10 px-3 py-1 text-xs font-semibold text-[#101876]">{s}</span>)}</div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="curriculum">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
              <div className="text-xs text-slate-500">Video / PDF / live-class content player arrives in <span className="font-semibold">Phase 6</span>. Structure preview:</div>
              {(payload.modules || []).length === 0 && <div className="text-center text-xs text-slate-500 py-6">Curriculum will be added by Steward.</div>}
              {(payload.modules || []).map((m) => (
                <div key={m.id} className="rounded-md border border-slate-200 bg-slate-50/50 p-4">
                  <div className="text-sm font-bold text-slate-900">{m.display_order}. {m.module_title}</div>
                  <div className="text-xs text-slate-500">{m.estimated_duration_minutes} min</div>
                  {m.lessons?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {m.lessons.map((l) => (
                        <div key={l.id} className="flex items-center justify-between rounded bg-white px-3 py-1.5 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 font-mono">{l.display_order}.</span>
                            <span className="font-semibold text-slate-800">{l.lesson_title}</span>
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">{l.lesson_type}</span>
                          </div>
                          <div className="text-slate-500">{l.estimated_duration_minutes} min</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="dept-map">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
              <div>
                <div className="text-sm font-bold text-slate-900">Departments applicable for this course</div>
                <div className="text-xs text-slate-500">Selected departments help you bulk-assign this course quickly.</div>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {depts.map((d) => (
                  <label key={d.id} className={`flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm ${selectedDepts.includes(d.id) ? "border-[#329112] bg-[#329112]/5" : "border-slate-200 bg-white"}`}>
                    <input type="checkbox" checked={selectedDepts.includes(d.id)} onChange={(e) => setSelectedDepts((prev) => e.target.checked ? [...prev, d.id] : prev.filter((x) => x !== d.id))} data-testid={`dept-map-${d.id}`} />
                    <span className="font-semibold text-slate-800">{d.department_name}</span>
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-xs">Type:</Label>
                <Select value={deptMand} onValueChange={setDeptMand}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="optional">Optional</SelectItem>
                    <SelectItem value="mandatory">Mandatory</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={saveDeptMapping} disabled={savingDept} className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="org-dept-map-save">
                  {savingDept ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save mapping
                </button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="desig-map">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
              <div>
                <div className="text-sm font-bold text-slate-900">Designations applicable for this course</div>
                <div className="text-xs text-slate-500">Useful when the course applies to a specific role, e.g. Staff Nurse.</div>
              </div>
              {desigs.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500">No designations yet. <Link className="font-semibold text-[#101876] hover:underline" to="/app/hospital/designations">Create designations →</Link></div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-3">
                  {desigs.map((d) => (
                    <label key={d.id} className={`flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm ${selectedDesigs.includes(d.id) ? "border-[#329112] bg-[#329112]/5" : "border-slate-200 bg-white"}`}>
                      <input type="checkbox" checked={selectedDesigs.includes(d.id)} onChange={(e) => setSelectedDesigs((prev) => e.target.checked ? [...prev, d.id] : prev.filter((x) => x !== d.id))} data-testid={`desig-map-${d.id}`} />
                      <span className="font-semibold text-slate-800">{d.designation_name}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-xs">Type:</Label>
                <Select value={desigMand} onValueChange={setDesigMand}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="optional">Optional</SelectItem>
                    <SelectItem value="mandatory">Mandatory</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={saveDesigMapping} disabled={savingDesig || desigs.length === 0} className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="org-desig-map-save">
                  {savingDesig ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save mapping
                </button>
              </div>
            </div>
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="removed" className="space-y-4">
              <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-4 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-900">
                  <div className="font-bold uppercase tracking-widest">Super Admin audit view</div>
                  <div className="mt-1">Permanent log of every staff un-enrolment for this course. Records are kept forever. Use <b>Re-assign</b> to restore access — the learner will resume from their last watched video.</div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">Recently removed staff</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700" data-testid="removed-total-count">
                      {removedList.length} total
                    </span>
                  </div>
                  <div className="relative w-full sm:w-72">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input value={removedQuery} onChange={(e) => setRemovedQuery(e.target.value)}
                      placeholder="Search staff or remover…" className="pl-8" data-testid="removed-search" />
                  </div>
                </div>

                {removedLoading ? (
                  <div className="py-10 text-center text-xs text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading audit log…
                  </div>
                ) : filteredRemoved.length === 0 ? (
                  <div className="py-10 text-center text-xs text-slate-500" data-testid="removed-empty">
                    {removedQuery ? "No records match your search." : "No staff have been removed from this course yet."}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Staff</th>
                          <th className="px-3 py-2 text-left">Removed by</th>
                          <th className="px-3 py-2 text-left">Removed on</th>
                          <th className="px-3 py-2 text-left">Original status</th>
                          <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredRemoved.map((r) => (
                          <tr key={r.id} data-testid={`removed-row-${r.id}`} className={r.reassigned_at ? "bg-emerald-50/40" : ""}>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-slate-900">{r.staff_name || "—"}</div>
                              <div className="text-[11px] text-slate-500">
                                {r.staff_email}{r.staff_employee_id ? ` · ${r.staff_employee_id}` : ""}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-slate-800">{r.removed_by_name || "—"}</div>
                              <div className="text-[11px] text-slate-500 capitalize">{(r.removed_by_role || "").replace(/_/g, " ")}</div>
                            </td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap text-xs">
                              {r.removed_at ? new Date(r.removed_at).toLocaleString() : "—"}
                            </td>
                            <td className="px-3 py-2">
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700 capitalize">
                                {(r.original_status || "—").replace(/_/g, " ")}
                              </span>
                              {typeof r.original_progress_percentage === "number" && (
                                <div className="mt-1 text-[10px] text-slate-500">{r.original_progress_percentage}% watched</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {r.reassigned_at ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold text-emerald-800" title={`Re-assigned on ${new Date(r.reassigned_at).toLocaleString()}`}>
                                  <CheckCircle2 className="h-3 w-3" /> Re-assigned
                                </span>
                              ) : (
                                <button
                                  onClick={() => reassignRemoved(r.id, r.staff_name)}
                                  disabled={reassigningId === r.id}
                                  className="inline-flex items-center gap-1 rounded-md border border-[#101876] bg-[#101876] px-2.5 py-1 text-[11px] font-bold text-white hover:bg-[#0c135c] disabled:opacity-50"
                                  data-testid={`removed-reassign-${r.id}`}
                                  title="Re-assign this course to this staff"
                                >
                                  {reassigningId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                                  Re-assign
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
      <Dialog open={!!assignDlg} onOpenChange={(o) => !o && setAssignDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Assign course {assignDlg === "all_staff" ? "to all staff" : `to ${assignDlg}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {assignDlg === "department" && (
              <div className="space-y-1">
                <Label>Department</Label>
                <Select value={assignForm.target_id} onValueChange={(v) => setAssignForm({ ...assignForm, target_id: v })}>
                  <SelectTrigger data-testid="assign-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.department_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {assignDlg === "designation" && (
              <div className="space-y-1">
                <Label>Designation</Label>
                <Select value={assignForm.target_id} onValueChange={(v) => setAssignForm({ ...assignForm, target_id: v })}>
                  <SelectTrigger data-testid="assign-desig-select"><SelectValue placeholder="Select designation" /></SelectTrigger>
                  <SelectContent>{desigs.map((d) => <SelectItem key={d.id} value={d.id}>{d.designation_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1"><Label>Due date (optional)</Label><Input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} /></div>
            <div className="space-y-1">
              <Label>Mandatory / Optional</Label>
              <Select value={assignForm.mandatory_or_optional} onValueChange={(v) => setAssignForm({ ...assignForm, mandatory_or_optional: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="optional">Optional</SelectItem>
                  <SelectItem value="mandatory">Mandatory</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAssignDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitAssign} disabled={assigning || (assignDlg !== "all_staff" && !assignForm.target_id)} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="assign-submit">
              {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Assign
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk enrol + Bill hospital dialog */}
      <Dialog open={billOpen} onOpenChange={setBillOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader><DialogTitle>Bulk enrol + Bill hospital</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Unit price (₹) *</Label>
                <Input type="number" min={0} value={billForm.unit_price}
                  onChange={(e) => setBillForm({ ...billForm, unit_price: e.target.value })}
                  data-testid="bulk-bill-price" placeholder="e.g. 1500" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Payment method *</Label>
                <Select value={billForm.payment_method}
                  onValueChange={(v) => setBillForm({ ...billForm, payment_method: v })}>
                  <SelectTrigger data-testid="bulk-bill-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="google_pay">Google Pay</SelectItem>
                    <SelectItem value="phonepe">PhonePe</SelectItem>
                    <SelectItem value="paytm">Paytm</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Reference / UTR</Label>
                <Input value={billForm.payment_reference}
                  onChange={(e) => setBillForm({ ...billForm, payment_reference: e.target.value })}
                  data-testid="bulk-bill-ref" placeholder="Optional" />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Staff to enrol</span>
                  <span className="rounded-full bg-[#329112] px-2 py-0.5 text-[10px] font-bold text-white" data-testid="bulk-bill-selected-count">
                    {selectedCount} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setAllStaff(true)} data-testid="bulk-bill-select-all" className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#101876]/40 hover:text-[#101876]">Select all</button>
                  <button type="button" onClick={() => setAllStaff(false)} data-testid="bulk-bill-clear-all" className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-rose-200 hover:text-rose-700">Clear</button>
                </div>
              </div>
              <div className="mt-2 relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={staffQuery} onChange={(e) => setStaffQuery(e.target.value)}
                  placeholder="Search by name, email, department, designation…"
                  className="pl-8" data-testid="bulk-bill-search" />
              </div>
              <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white" data-testid="bulk-bill-staff-list">
                {filteredStaff.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-500">No staff match.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredStaff.map((s) => (
                      <li key={s.id} className="flex items-center gap-3 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!selectedStaff[s.id]}
                          onChange={() => toggleStaff(s.id)}
                          className="h-4 w-4"
                          data-testid={`bulk-bill-staff-${s.id}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{s.full_name || s.email}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {s.email}{s.department_name ? ` · ${s.department_name}` : ""}{s.designation ? ` · ${s.designation}` : ""}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {selectedCount > 0 && Number(billForm.unit_price) > 0 && (
              <div className="rounded-lg border-2 border-[#329112] bg-[#329112]/5 p-3 text-sm" data-testid="bulk-bill-summary">
                <CheckCircle2 className="inline h-4 w-4 text-[#329112] mr-1" />
                Billing hospital for <b>{selectedCount}</b> staff × ₹{Number(billForm.unit_price).toLocaleString("en-IN")} = <b>₹{(selectedCount * Number(billForm.unit_price)).toLocaleString("en-IN")}</b> via <b className="capitalize">{billForm.payment_method.replace(/_/g, " ")}</b>.
              </div>
            )}

            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={billForm.send_email}
                onChange={(e) => setBillForm({ ...billForm, send_email: e.target.checked })}
                data-testid="bulk-bill-email" />
              Email invoice PDF to the institute
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setBillOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitBulkBill} disabled={billing || selectedCount === 0 || !Number(billForm.unit_price)}
              className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-4 py-2 text-sm font-bold text-white hover:bg-[#256b0d] disabled:opacity-50"
              data-testid="bulk-bill-submit">
              {billing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
              Create invoice · {selectedCount} staff
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Individual "Assign to selected staff" dialog */}
      <Dialog open={assignIndivOpen} onOpenChange={setAssignIndivOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Assign to selected staff</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Assignment type *</Label>
                <Select value={assignIndivMand} onValueChange={setAssignIndivMand}>
                  <SelectTrigger data-testid="assign-indiv-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="optional">Optional</SelectItem>
                    <SelectItem value="mandatory">Mandatory</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Due date</Label>
                <Input type="date" value={assignIndivDue} onChange={(e) => setAssignIndivDue(e.target.value)} data-testid="assign-indiv-due" />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Staff to assign</span>
                  <span className="rounded-full bg-[#101876] px-2 py-0.5 text-[10px] font-bold text-white" data-testid="assign-indiv-count">
                    {selectedCount} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setAllStaff(true)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#101876]/40 hover:text-[#101876]">Select all</button>
                  <button type="button" onClick={() => setAllStaff(false)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-rose-200 hover:text-rose-700">Clear</button>
                </div>
              </div>
              <div className="mt-2 relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={staffQuery} onChange={(e) => setStaffQuery(e.target.value)}
                  placeholder="Search by name, email, department, designation…"
                  className="pl-8" data-testid="assign-indiv-search" />
              </div>
              <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white">
                {filteredStaff.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-500">No staff match.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredStaff.map((s) => (
                      <li key={s.id} className="flex items-center gap-3 px-3 py-2">
                        <input type="checkbox" checked={!!selectedStaff[s.id]} onChange={() => toggleStaff(s.id)} className="h-4 w-4"
                          data-testid={`assign-indiv-staff-${s.id}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{s.full_name || s.email}</div>
                          <div className="text-[11px] text-slate-500 truncate">{s.email}{s.department_name ? ` · ${s.department_name}` : ""}{s.designation ? ` · ${s.designation}` : ""}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAssignIndivOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitIndividualAssign} disabled={assigningIndiv || selectedCount === 0}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-bold text-white hover:bg-[#0c135c] disabled:opacity-50"
              data-testid="assign-indiv-submit">
              {assigningIndiv ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Assign · {selectedCount} staff
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "View assignments" dialog with per-row Un-assign */}
      <Dialog open={assignmentsOpen} onOpenChange={setAssignmentsOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Current assignments</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={assignmentsQuery} onChange={(e) => setAssignmentsQuery(e.target.value)}
                placeholder="Search by name / email / employee ID…"
                className="pl-8" data-testid="assignments-search" />
            </div>
            {assignmentsLoading ? (
              <div className="py-8 text-center text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
            ) : filteredAssignments.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-500">No active assignments{assignmentsQuery ? " match your search" : ""}.</div>
            ) : (
              <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200 bg-white">
                <ul className="divide-y divide-slate-100">
                  {filteredAssignments.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`assignment-row-${a.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 truncate">{a.user?.full_name || a.user?.email || "?"}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {a.user?.email}{a.user?.employee_id ? ` · ${a.user.employee_id}` : ""}
                          {" · "}<span className="capitalize">{(a.status || "").replace(/_/g, " ")}</span>
                          {a.mandatory_or_optional === "mandatory" && <span className="ml-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">MANDATORY</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => unassignOne(a.id, a.user?.full_name)}
                        disabled={unassigningId === a.id}
                        className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        data-testid={`assignment-unassign-${a.id}`}
                        title="Remove this course from this staff"
                      >
                        {unassigningId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setAssignmentsOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function ParaBlock({ title, text }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</div>
      <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{text}</p>
    </div>
  );
}
